import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import ProjectView from "./components/ProjectView";
import ProjectEmails from "./components/ProjectEmails";
import EmailSettings from "./components/EmailSettings";
import RoleManagement from "./components/RoleManagement";
import ProtectedRoute from "./components/ProtectedRoute";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { isCustomerPro, CUSTOMER_PRO_LANDING } from "./lib/roles";

// New Module System
import ModuleSelector from "./pages/ModuleSelector";
import SafetyDashboard from "./pages/SafetyDashboard";
import POManagement from "./pages/POManagement";
import PODetail from "./pages/PODetail";
import SafetyQRGeneration from "./pages/SafetyQRGeneration";
import SafetyQRHistory from "./pages/SafetyQRHistory";
import SafetyQRSettings from "./pages/SafetyQRSettings";
import AuditModule from "./pages/AuditModule";
import ProductCatalog from "./pages/ProductCatalog";
import BOMBuilder from "./pages/BOMBuilder";
import CustomersMaster from "./pages/CustomersMaster";
import PowderMasters from "./pages/PowderMasters";
import ProductionReports from "./pages/ProductionReports";
import ProductionReportForm from "./pages/ProductionReportForm";
import ProductionReportView from "./pages/ProductionReportView";
import ProductionLive from "./pages/ProductionLive";
import PendingProductionTracker from "./pages/PendingProductionTracker";
import ProductionApprovals from "./pages/ProductionApprovals";
import PackingLists from "./pages/PackingLists";
import ShippingBills from "./pages/ShippingBills";
import ManagementReport from "./pages/ManagementReport";
import ProductDemandReport from "./pages/ProductDemandReport";
import Pallets from "./pages/Pallets";
import InventoryDashboard from "./pages/InventoryDashboard";
import InventoryReconcile from "./pages/InventoryReconcile";
import StockCount from "./pages/StockCount";
import InventoryCutoverSetup from "./pages/InventoryCutoverSetup";
import ContainerPlanner from "./pages/ContainerPlanner";
import MobileApp from "./mobile/MobileApp";
import VisitorApp from "./visitor/VisitorApp";
import VisitorsDashboard from "./pages/VisitorsDashboard";
import CRMPipeline from "./pages/CRMPipeline";
import PendingAttachments from "./pages/PendingAttachments";
import ProjectsTrash from "./pages/ProjectsTrash";

import "@/App.css";

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Impersonation: the original admin session stored while impersonating
  const isImpersonating = !!localStorage.getItem("admin_token");

  // Admin gate: only super-admins (or legacy ADMIN role) reach the Administration module
  const isAdmin = !!user && ((user.role_ids || []).includes("role-super-admin") || user.role === "ADMIN");

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user");
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  // Heartbeat: ping the server every 60s while the user is logged in
  useEffect(() => {
    if (!user) return;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const sendHeartbeat = () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      fetch(`${BACKEND_URL}/api/users/heartbeat`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    };
    sendHeartbeat();
    const id = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(id);
  }, [user]);

  const handleLogin = (userData, token) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    // Clear impersonation data too
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const handleImpersonate = (targetUserData, targetToken) => {
    // Save current admin session
    localStorage.setItem("admin_token", localStorage.getItem("token"));
    localStorage.setItem("admin_user", localStorage.getItem("user"));
    // Switch to impersonated user
    localStorage.setItem("token", targetToken);
    localStorage.setItem("user", JSON.stringify(targetUserData));
    setUser(targetUserData);
  };

  const handleExitImpersonation = () => {
    // Restore admin session
    const adminToken = localStorage.getItem("admin_token");
    const adminUser = localStorage.getItem("admin_user");
    if (adminToken && adminUser) {
      localStorage.setItem("token", adminToken);
      localStorage.setItem("user", adminUser);
      setUser(JSON.parse(adminUser));
    }
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <BrowserRouter>
      {/* Impersonation Banner - shown on ALL pages when impersonating */}
      {isImpersonating && user && (
        <ImpersonationBanner
          impersonatedUser={user}
          onExit={handleExitImpersonation}
        />
      )}

      <Routes>
        {/* Auth Routes */}
        <Route
          path="/login"
          element={
            !user
              ? <Login onLogin={handleLogin} />
              : <Navigate to={isCustomerPro(user) ? CUSTOMER_PRO_LANDING : "/select-module"} />
          }
        />
        
        {/* Module Selector — CUSTOMER_PRO bypasses this and lands directly on the planner */}
        <Route
          path="/select-module"
          element={
            user
              ? (isCustomerPro(user)
                  ? <Navigate to={CUSTOMER_PRO_LANDING} replace />
                  : <ModuleSelector user={user} isAdmin={isAdmin} onLogout={handleLogout} />)
              : <Navigate to="/login" />
          }
        />

        {/* Project Module Routes - Protected by module.project permission */}
        <Route
          path="/"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="module.project">
                <Dashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/project/:projectId"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="module.project">
                <ProjectView user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/project/:projectId/emails"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="module.project">
                <ProjectEmails user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/email-settings"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="module.project">
                <EmailSettings user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/role-management"
          element={user ? (isAdmin ? <RoleManagement user={user} onLogout={handleLogout} onImpersonate={handleImpersonate} /> : <Navigate to="/select-module" replace />) : <Navigate to="/login" />}
        />
        <Route
          path="/audit"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="audit.view">
                <AuditModule user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Cross-project Pending Email Attachments */}
        <Route
          path="/projects/pending-attachments"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="email.attachments.classify">
                <PendingAttachments user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Project Trash (Super Admin / project.delete holders) */}
        <Route
          path="/projects/trash"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="project.delete">
                <ProjectsTrash user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Safety Equipment Module Routes - Protected by module.safety_equipment permission */}
        <Route
          path="/safety"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="module.safety_equipment">
                <SafetyDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/po"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="module.safety_equipment">
                <POManagement user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/po/:poId/detail"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="module.safety_equipment">
                <PODetail user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/qr"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="module.safety_equipment">
                <SafetyQRGeneration user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/history"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="module.safety_equipment">
                <SafetyQRHistory user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/settings"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="module.safety_equipment">
                <SafetyQRSettings user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/catalog"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="catalog.view">
                <ProductCatalog user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/po/:poId/generate-bom"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="bom.upload">
                <BOMBuilder user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/customers"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="customer.view">
                <CustomersMaster user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/powder-masters"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="production.view">
                <PowderMasters user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/packing-lists"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="packing_list.view">
                <PackingLists user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/shipping-bills"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="packing_list.view">
                <ShippingBills user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/management-report"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="management_report.view">
                <ManagementReport user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/product-demand"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="management_report.view">
                <ProductDemandReport user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/pallets"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="pallet.view">
                <Pallets user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/inventory"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="inventory.view">
                <InventoryDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/inventory/setup"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="inventory.manage">
                <InventoryCutoverSetup user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/inventory/reconcile"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="inventory.view">
                <InventoryReconcile user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/inventory/stock-count"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission={["inventory.view", "stock.count"]}>
                <StockCount user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/container-planner"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="container.plan">
                <ContainerPlanner user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        {/* Mobile PWA — its own auth + routing. No outer user gate. */}
        <Route path="/mobile/*" element={<MobileApp />} />
        {/* Exhibition Visitor Capture PWA — separate PWA, its own auth + routing. */}
        <Route path="/visitors/*" element={<VisitorApp />} />
        {/* Centralised desktop dashboard for captured leads. */}
        <Route
          path="/leads"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="visitor.capture">
                <VisitorsDashboard user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/crm"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="lead.view">
                <CRMPipeline user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/production-reports"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="production.view">
                <ProductionReports user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/production-live"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="production.view">
                <ProductionLive user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/production-reports/approvals"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="production.approve">
                <ProductionApprovals user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/production-reports/new"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="production.create">
                <ProductionReportForm user={user} onLogout={handleLogout} mode="create" />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/production-reports/:reportId/edit"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="production.create">
                <ProductionReportForm user={user} onLogout={handleLogout} mode="edit" />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/production-reports/:reportId"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="production.view">
                <ProductionReportView user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/safety/pending-production"
          element={
            user ? (
              <ProtectedRoute user={user} requiredPermission="production.view">
                <PendingProductionTracker user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/select-module" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;

